import _ from 'lodash'
import echarts from 'echarts'
import { randomColor } from '@/utils/common'
import moment from 'moment'

const getMonthDays = time => {
  const endTime = moment(time)
    .endOf('month')
    .format('DD')
  const days = []
  for (let i = 1; i <= +endTime; i++) {
    days.push(i)
  }
  return days
}

const initLine = (
  text,
  el,
  legendData,
  xAxisData,
  yAxisName,
  seriesData,
  xAxisName,
  needShadow = false
) => {
  // text: 传入'', 标题
  // el: 传入'', chart元素id
  // legendData: 传入[], 内容是折线名
  // xAxisData: 传入[], X轴的刻度
  // yAxisName: 传入'',Y轴的名字
  // seriesData: 传入[[], []], 渲染的数据
  // needShadow: 折线需不需要阴影
  legendData = _.map(legendData, item => {
    return {
      name: item,
      icon: 'rect'
    }
  })
  const series = _.map(seriesData, (item, index) => {
    const color = randomColor('dark')
    const data = {
      name: legendData[index] && legendData[index].name,
      data: item,
      color,
      type: 'line',
      symbol: 'none',
      itemStyle: {
        normal: {
          lineStyle: {
            width: 1.5
          }
        }
      }
    }
    if (needShadow) {
      data.areaStyle = {
        opacity: 0.2,
        normal: {
          color: new echarts.graphic.LinearGradient(
            0,
            0,
            0,
            1,
            [
              {
                offset: 0,
                color
              },
              {
                offset: 1,
                color: '#1c2027'
              }
            ],
            false
          )
        }
      }
    }
    return data
  })
  return {
    text,
    el,
    options: {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        itemWidth: 12,
        itemHeight: 8,
        textStyle: {
          color: '#fff'
        },
        data: legendData,
        left: 70,
        top: 16,
        type: 'scroll',
        pageIconColor: '#aaa',
        pageIconInactiveColor: '#2f4554'
      },
      grid: {
        left: '3%',
        right: '60',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        name: xAxisName,
        axisLine: {
          lineStyle: {
            color: '#607cc5'
          }
        },
        axisLabel: {
          interval: 4
        },
        axisTick: {
          show: false
        },
        boundaryGap: false,
        data: xAxisData
      },
      yAxis: {
        type: 'value',
        name: yAxisName,
        nameTextStyle: {
          align: 'right',
          padding: [0, -20, 0, 0]
        },
        nameGap: 24,
        axisLine: {
          show: true,
          lineStyle: {
            color: '#607cc5'
          }
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: '#495482'
          }
        }
      },
      series
    }
  }
}

const initPie = (text, el, subtext, data) => {
  const seriesData = data.reduce((arr, item) => {
    if (item.value > 0) {
      arr.push({
        ...item,
        itemStyle: {
          color: randomColor('dark')
        }
      })
    }
    return arr
  }, [])
  return {
    text,
    el,
    options: {
      title: {
        subtext,
        top: 10,
        subtextStyle: {
          color: '#607cc5'
        }
      },
      tooltip: {
        trigger: 'item'
      },
      series: [
        {
          name: text,
          type: 'pie',
          radius: '52%',
          center: ['48%', '60%'],
          data: seriesData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          label: {
            show: true,
            formatter(params) {
              const { value, name } = params.data
              return `${name} ${value}个`
            }
          }
        }
      ]
    }
  }
}

const initMap = (
  text,
  el,
  yesterdayChildStat,
  regionFullName,
  data,
  geoJson
) => {
  const length = data.length
  const maxCount = _.get(data, `${length - 1}.value`, 0) || 10
  const minCount = _.get(data, '0.value', 0)
  return {
    text,
    el,
    options: {
      tooltip: {
        trigger: 'item',
        formatter: params => {
          const { data = {}, name } = params
          const curArea = _.get(yesterdayChildStat, `${data.name}.0`, {})
          return `${data.name || name}<br/>激活学生：${data.value ||
            0}<br/>教师数：${(curArea && curArea.teacherTotal) || 0}`
        }
      },
      visualMap: {
        min: minCount,
        max: maxCount,
        text: ['高', '低'],
        bottom: 100,
        left: 14,
        realtime: false,
        calculable: true,
        textStyle: {
          color: '#fff'
        },
        inRange: {
          color: ['#c3d5ff', '#9db7ff', '#779eff', '#5081ff', '#2a65ff']
        }
      },
      series: [
        {
          name: regionFullName,
          type: 'map',
          mapType: 'MAP_NAME',
          label: {
            show: true,
            color: '#bda29a'
          },
          data
        }
      ]
    },
    geoJson
  }
}

const initBar = (text, el, xAxisData, yAxisName, seriesData) => {
  seriesData = _.map(seriesData, item => {
    return {
      data: item,
      type: 'bar',
      itemStyle: {
        normal: {
          color: params => {
            const colors = _.map(xAxisData, () => randomColor('dark'))
            return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: colors[params.dataIndex]
              },
              {
                offset: 1,
                color: '#272c66'
              }
            ])
          }
        }
      }
    }
  })
  return {
    text,
    el,
    options: {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '4%',
        right: '3%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        axisLine: {
          lineStyle: {
            color: '#607cc5'
          }
        },
        axisTick: {
          show: false
        },
        data: xAxisData
      },
      yAxis: {
        type: 'value',
        name: yAxisName,
        nameTextStyle: {
          align: 'right'
        },
        nameGap: 24,
        axisLine: {
          show: true,
          lineStyle: {
            color: '#607cc5'
          }
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: '#495482'
          }
        }
      },
      series: seriesData
    }
  }
}

const initLargeMap = (
  text,
  el,
  yesterdayChildStat,
  regionFullName,
  data,
  geoJson
) => {
  const length = data.length
  const maxCount = _.get(data, `${length - 1}.value`, 0) || 10
  const minCount = _.get(data, '0.value', 0)
  return {
    text,
    el,
    options: {
      tooltip: {
        trigger: 'item',
        formatter: params => {
          const { data = {}, name } = params
          const curArea = _.get(yesterdayChildStat, `${data.name}.0`, {})
          return `${data.name || name}<br/>激活学生：${data.value ||
            0}<br/>教师数：${(curArea && curArea.teacherTotal) || 0}`
        }
      },
      visualMap: {
        min: minCount,
        max: maxCount,
        text: ['高', '低'],
        bottom: 'center',
        left: 20,
        realtime: false,
        calculable: true,
        textStyle: {
          color: '#000'
        },
        inRange: {
          color: ['#b3dfd8', '#34608a']
        }
      },
      series: [
        {
          name: regionFullName,
          type: 'map',
          mapType: 'MAP_NAME',
          label: {
            show: true,
            color: '#000',
            fontSize: 20,
            formatter: params => {
              const { data = {}, name } = params
              const [left] = _.split(params.name || name, '特别行政区')
              if (name) {
                return `${left}: ${data.value || 0}`
              }
            }
          },
          data
        }
      ]
    },
    geoJson
  }
}

export { getMonthDays, initLine, initPie, initMap, initBar, initLargeMap }
